import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { 
  Play, Pause, SkipForward, SkipBack, Volume2, VolumeX,
  GraduationCap, CheckCircle2, Circle, Loader2, X, 
  ChevronRight, BookOpen, Clock, ListChecks
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE_URL as API } from '../utils/apiConfig';

export default function TrainingModule({ moduleId = 'command_center', onClose }) {
  const [module, setModule] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [audioProgress, setAudioProgress] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState(null);
  
  const audioRef = useRef(null);
  const audioCache = useRef({});

  // Fetch module data
  useEffect(() => {
    const fetchModule = async () => {
      try {
        const response = await fetch(`${API}/api/training/module/${moduleId}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setModule(data);
        }
      } catch (error) {
        console.error('Error fetching training module:', error);
        toast.error('Failed to load training module');
      }
    };
    
    fetchModule();
  }, [moduleId]);

  const currentStep = module?.steps?.[currentStepIndex];

  // Load audio for current step
  const loadAudio = useCallback(async (stepId) => {
    if (audioCache.current[stepId]) {
      return audioCache.current[stepId];
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${API}/api/training/audio-base64/${moduleId}/${stepId}?voice=nova`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        const audioUrl = `data:audio/mpeg;base64,${data.audio_base64}`;
        audioCache.current[stepId] = audioUrl;
        return audioUrl;
      }
    } catch (error) {
      console.error('Error loading audio:', error);
      toast.error('Failed to load audio narration');
    } finally {
      setIsLoading(false);
    }
    return null;
  }, [moduleId]);

  // Highlight element on page
  useEffect(() => {
    if (currentStep?.element && currentStep?.highlight) {
      const element = document.querySelector(currentStep.element);
      if (element) {
        // Add highlight overlay
        element.style.transition = 'all 0.3s ease';
        element.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.5), 0 0 20px rgba(139, 92, 246, 0.3)';
        element.style.zIndex = '1000';
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedElement(element);
      }
    }

    return () => {
      if (highlightedElement) {
        highlightedElement.style.boxShadow = '';
        highlightedElement.style.zIndex = '';
      }
    };
  }, [currentStep, highlightedElement]);

  // Play current step
  const playStep = async () => {
    if (!currentStep) return;

    const audioUrl = await loadAudio(currentStep.id);
    if (audioUrl && audioRef.current) {
      audioRef.current.src = audioUrl;
      audioRef.current.muted = isMuted;
      await audioRef.current.play();
      setIsPlaying(true);
    }
  };

  // Handle audio events
  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCompletedSteps(prev => new Set([...prev, currentStep?.id]));
    setAudioProgress(100);
    
    // Auto-advance to next step after short delay
    if (currentStepIndex < (module?.steps?.length || 0) - 1) {
      setTimeout(() => {
        nextStep();
      }, 1500);
    }
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setAudioProgress(progress);
    }
  };

  // Navigation
  const nextStep = () => {
    if (currentStepIndex < (module?.steps?.length || 0) - 1) {
      setCurrentStepIndex(prev => prev + 1);
      setAudioProgress(0);
      setIsPlaying(false);
    }
  };

  const prevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      setAudioProgress(0);
      setIsPlaying(false);
    }
  };

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      playStep();
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
    }
  };

  const goToStep = (index) => {
    setCurrentStepIndex(index);
    setAudioProgress(0);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
  };

  if (!module) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-violet-500 mb-4" />
            <p className="text-muted-foreground">Loading training module...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progressPercent = ((currentStepIndex + 1) / module.steps.length) * 100;

  return (
    <>
      {/* Hidden audio element */}
      <audio 
        ref={audioRef}
        onEnded={handleAudioEnded}
        onTimeUpdate={handleAudioTimeUpdate}
      />

      {/* Training Overlay */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => {}} />
      
      {/* Training Panel */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
        <Card className="max-w-4xl mx-auto bg-background/95 backdrop-blur border-violet-500/30 shadow-2xl">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-500/20">
                  <GraduationCap className="w-6 h-6 text-violet-500" />
                </div>
                <div>
                  <CardTitle className="text-lg">{module.title}</CardTitle>
                  <CardDescription className="flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    {module.duration}
                    <span className="mx-1">•</span>
                    Step {currentStepIndex + 1} of {module.steps.length}
                  </CardDescription>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Progress bar */}
            <div className="mt-3">
              <Progress value={progressPercent} className="h-1" />
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Current Step Info */}
            <div className="flex gap-4">
              {/* Step Navigation */}
              <div className="w-48 shrink-0">
                <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                  <ListChecks className="w-3 h-3" /> Steps
                </p>
                <div className="space-y-1 max-h-40 overflow-y-auto pr-2">
                  {module.steps.map((step, idx) => (
                    <button
                      key={step.id}
                      onClick={() => goToStep(idx)}
                      className={`w-full flex items-center gap-2 p-2 rounded-lg text-left text-sm transition-colors ${
                        idx === currentStepIndex 
                          ? 'bg-violet-500/20 text-violet-300' 
                          : completedSteps.has(step.id)
                            ? 'text-green-400 hover:bg-muted/50'
                            : 'text-muted-foreground hover:bg-muted/50'
                      }`}
                    >
                      {completedSteps.has(step.id) ? (
                        <CheckCircle2 className="w-4 h-4 shrink-0 text-green-500" />
                      ) : idx === currentStepIndex ? (
                        <div className="w-4 h-4 rounded-full border-2 border-violet-500 shrink-0" />
                      ) : (
                        <Circle className="w-4 h-4 shrink-0" />
                      )}
                      <span className="truncate">{step.title}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Current Step Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4 text-violet-400" />
                  <h3 className="font-semibold">{currentStep?.title}</h3>
                </div>
                
                <div className="bg-muted/30 rounded-lg p-4 max-h-32 overflow-y-auto">
                  <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                    {currentStep?.script}
                  </p>
                </div>
                
                {/* Audio Progress */}
                <div className="mt-3">
                  <Progress value={audioProgress} className="h-1" />
                </div>
              </div>
            </div>
            
            {/* Controls */}
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={prevStep}
                  disabled={currentStepIndex === 0}
                >
                  <SkipBack className="w-5 h-5" />
                </Button>
                
                <Button 
                  size="lg"
                  onClick={togglePlay}
                  disabled={isLoading}
                  className="w-32 bg-violet-600 hover:bg-violet-700"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isPlaying ? (
                    <><Pause className="w-5 h-5 mr-2" /> Pause</>
                  ) : (
                    <><Play className="w-5 h-5 mr-2" /> Play</>
                  )}
                </Button>
                
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={nextStep}
                  disabled={currentStepIndex === module.steps.length - 1}
                >
                  <SkipForward className="w-5 h-5" />
                </Button>
                
                <Button variant="ghost" size="icon" onClick={toggleMute}>
                  {isMuted ? (
                    <VolumeX className="w-5 h-5" />
                  ) : (
                    <Volume2 className="w-5 h-5" />
                  )}
                </Button>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {completedSteps.size} of {module.steps.length} completed
                </span>
                {currentStepIndex === module.steps.length - 1 && completedSteps.size === module.steps.length && (
                  <Button onClick={onClose} className="bg-green-600 hover:bg-green-700">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Finish Training
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

// Training Launcher Button Component
export function TrainingLauncherButton({ moduleId, children }) {
  const [showTraining, setShowTraining] = useState(false);

  return (
    <>
      <Button 
        variant="outline" 
        onClick={() => setShowTraining(true)}
        className="gap-2"
      >
        <GraduationCap className="w-4 h-4" />
        {children || 'Start Training'}
      </Button>
      
      {showTraining && (
        <TrainingModule 
          moduleId={moduleId} 
          onClose={() => setShowTraining(false)} 
        />
      )}
    </>
  );
}

// Training Menu Component
export function TrainingMenu() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(null);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await fetch(`${API}/api/training/modules`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setModules(data.modules || []);
        }
      } catch (error) {
        console.error('Error fetching modules:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchModules();
  }, []);

  if (loading) {
    return (
      <div className="p-4 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {modules.map((module) => (
          <Card 
            key={module.id}
            className="cursor-pointer hover:border-violet-500/50 transition-colors"
            onClick={() => setActiveModule(module.id)}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-500/20">
                  <GraduationCap className="w-5 h-5 text-violet-500" />
                </div>
                <div>
                  <h4 className="font-medium">{module.title}</h4>
                  <p className="text-sm text-muted-foreground">
                    {module.description}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {module.duration} • {module.steps_count} steps
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Card>
        ))}
      </div>

      {activeModule && (
        <TrainingModule 
          moduleId={activeModule} 
          onClose={() => setActiveModule(null)} 
        />
      )}
    </>
  );
}
